if ( typeof customElements.get('product-reviews-card') == 'undefined' ) {

  class ProductReviewsCard extends HTMLElement {

    connectedCallback() {

      this.modal = this.querySelector('modal-box') || document.getElementById(this.dataset.modalId);
      if ( ! this.modal ) return;

      // Fixed-position modals must not live inside the section (transformed/overflow ancestors)
      if ( this.modal.parentElement !== document.body ) {
        document.body.appendChild(this.modal);
      }

      this.widget = this.modal.querySelector('[data-reviews-widget]');
      this.latestReview = this.querySelector('[data-latest-review]');
      this.pendingLatest = !! this.latestReview;
      this.pendingWrite = false;

      this.querySelectorAll('[data-reviews-open]').forEach(button => {
        button.addEventListener('click', () => {
          this.open(button, button.hasAttribute('data-reviews-write'));
        });
      });

      // Judge.me renders the widget asynchronously, so watch it until we have what we need
      this.observer = new MutationObserver(() => this.sync());
      this.observer.observe(this.widget, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
      this.sync();

    }

    disconnectedCallback() {
      if ( this.observer ) this.observer.disconnect();
      // Section re-render in the theme editor: drop the modal we moved to <body>
      if ( this.modal && this.modal.parentElement === document.body ) this.modal.remove();
    }

    open(trigger, write) {
      window.lastFocusedElm = trigger;
      this.modal.show();
      if ( write ) {
        this.pendingWrite = true;
        this.sync();
      }
    }

    sync() {
      if ( ! this.formPreloaded ) this.preloadWriteForm();
      if ( this.pendingLatest && this.renderLatestReview() ) this.pendingLatest = false;
      if ( this.pendingWrite && this.openWriteForm() ) this.pendingWrite = false;
    }

    renderLatestReview() {

      const reviews = [...this.widget.querySelectorAll('.jdgm-rev')];
      if ( ! reviews.length ) return false;

      // Judge.me timestamps look like "2025-03-14 09:21:07 UTC"
      const time = review => {
        const stamp = review.querySelector('.jdgm-rev__timestamp')?.dataset.content || '';
        return Date.parse(stamp.replace(' UTC', 'Z').replace(' ', 'T')) || 0;
      };
      const latest = reviews.reduce((newest, review) => time(review) > time(newest) ? review : newest);

      const text = (latest.querySelector('.jdgm-rev__body')?.textContent || latest.querySelector('.jdgm-rev__title')?.textContent || '').trim();
      const author = (latest.querySelector('.jdgm-rev__author')?.textContent || '').trim();
      if ( ! text ) return true;

      this.latestReview.querySelector('[data-latest-review-body]').textContent = text;
      const authorElm = this.latestReview.querySelector('[data-latest-review-author]');
      authorElm.textContent = author;
      authorElm.parentElement.hidden = ! author;
      this.latestReview.hidden = false;

      return true;

    }

    // Judge.me lazy-loads its write-review form on the first click of its button and
    // only opens the popup on the second click. Load it up front (same calls Judge.me
    // makes itself for #judgeme links) so the first click opens the popup.
    // Wait until Judge.me has set the widget up: form.js is what shows the (server-rendered,
    // hidden) write button, translates it and binds the popup — but only for widgets that
    // are already set up when it runs.
    preloadWriteForm() {
      const jdgm = window.jdgm;
      const setUp = this.widget.matches('.jdgm--done-setup-widget') || this.widget.querySelector('.jdgm--done-setup-widget');
      if ( ! jdgm || ! jdgm.loadScript || ! jdgm.CDN_HOST || ! setUp || ! this.widget.querySelector('.jdgm-write-rev-link') ) return;
      this.formPreloaded = true;
      jdgm.loadScript(jdgm.CDN_HOST + 'widget/form.js');
      jdgm.loadScript(jdgm.CDN_HOST + 'widget/write_review_modal.js');
      if ( jdgm.loadCSS && jdgm.widgetPath ) jdgm.loadCSS(jdgm.widgetPath('form.css'));
    }

    openWriteForm() {

      const link = this.widget.querySelector('.jdgm-write-rev-link');
      if ( ! link ) return false;

      // Inline form (expands inside the widget) only when Judge.me isn't set to its popup flow.
      // Don't infer it from the DOM: Judge.me's form.js adds a hidden .jdgm-form-wrapper even in popup mode.
      const popupFlow = !! ( window.jdgmSettings && window.jdgmSettings.modal_write_review_flow );
      const form = popupFlow ? null : this.widget.querySelector('.jdgm-form-wrapper');

      if ( form ) {
        if ( getComputedStyle(form).display === 'none' ) link.click();
      }
      // Popup layout: never click Judge.me's link for the visitor — it would open
      // Judge.me's popup on top of ours. Just bring their write button into view.

      setTimeout(() => {
        ( form || link ).scrollIntoView({ behavior: 'smooth', block: 'start' });
        if ( ! form ) link.focus({ preventScroll: true });
      }, 350);

      return true;

    }

  }

  customElements.define('product-reviews-card', ProductReviewsCard);

}
